































import SearchBuilder from "@/builder/SearchBuilder";
import { debounceProcess } from "@/helpers/debounce";
import useInventory from "@/hooks/useInventory";
import { Option } from "@/models/class/option.class";
import { RequestQueryParams } from "@/models/class/request-query-params.class";
import { InventoryLineResponseDto } from "@/models/interface/inventory";
import { LabelInValue } from "@/types";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component
export default class SelectProductInventory extends Vue {
  @Prop({ required: false, type: Object, default: undefined })
  readonly value!: LabelInValue;

  @Prop({ required: false, type: Boolean, default: false })
  disabled!: boolean;

  @Prop({ required: false, type: String, default: "code" })
  optionLabel!: "code" | "name";

  @Prop({ required: false, type: String, default: "" })
  warehouseLocationId!: string;

  @Prop({ required: false, type: String, default: "" })
  productCategoryId!: string;

  @Prop({ required: false, type: String, default: "" })
  productBrand!: string;

  @Prop({ required: false, type: Boolean, default: false })
  readonly useSerialNumber!: boolean;

  options: Array<Option<InventoryLineResponseDto>> = [];
  loading = false;
  requestSequence = 0;

  created(): void {
    this.onSearch = debounceProcess(this.onSearch);

    this.preFetch();
  }

  @Watch("useSerialNumber")
  onChangeUseSerialNumber(): void {
    this.options = [];
    this.preFetch();
  }

  @Watch("warehouseLocationId")
  onChangeWarehouse(): void {
    this.options = [];
    this.preFetch();
  }

  @Watch("productCategoryId")
  onChangeCategory(): void {
    this.options = [];
    this.preFetch();
  }

  @Watch("productBrand")
  onChangeBrand(): void {
    this.options = [];
    this.preFetch();
  }

  get isMapAsCode(): boolean {
    return this.optionLabel === "code";
  }

  preFetch(): void {
    if (!this.warehouseLocationId) {
      this.options = [];
      return;
    }

    const params = new RequestQueryParams();
    params.search = this.buildQuery();

    this.fetchOptions(params);
  }

  fetchOptions(params = new RequestQueryParams()): void {
    const { findAllInventoryLine, toOptions } = useInventory();

    params.zeroStock = true;
    const requestId = ++this.requestSequence;

    this.loading = true;
    findAllInventoryLine(params)
      .then(({ data }) => {
        if (requestId !== this.requestSequence) return;
        this.options = toOptions(data, this.optionLabel);
      })
      .catch(() => {
        if (requestId === this.requestSequence) {
          this.options = [];
        }
      })
      .finally(() => {
        if (requestId === this.requestSequence) {
          this.loading = false;
        }
      });
  }

  onChange(e?: LabelInValue): void {
    this.$emit("input", e);
    this.$emit("change", e);
    this.$emit("update:meta", this.findOption(e));
  }

  onSearch(val = ""): void {
    if (!this.warehouseLocationId) {
      this.options = [];
      return;
    }

    const { searchBy } = useInventory();
    const params = new RequestQueryParams();
    const builder = new SearchBuilder();
    const query: Array<string> = [];
    const baseQuery = this.buildQuery();

    if (baseQuery) {
      query.push(baseQuery);
    }

    if (val) {
      query.unshift(searchBy({ productCode: val, productName: val }));
    }

    params.search = query.join(builder.AND);

    this.fetchOptions(params);
  }

  findOption(
    value?: LabelInValue
  ): Option<InventoryLineResponseDto> | undefined {
    return this.options.find(e => value?.key === e.value);
  }

  buildQuery(): string {
    const builder = new SearchBuilder();
    const query: Array<string> = [];

    if (this.useSerialNumber) {
      query.push(builder.push(["product.trackAsAsset", "true"]).build());
    }

    if (this.warehouseLocationId) {
      query.push(
        builder
          .push(["warehouseLocation.secureId", this.warehouseLocationId])
          .build()
      );
    }

    if (this.productCategoryId) {
      query.push(
        builder
          .push(["product.category.secureId", this.productCategoryId])
          .build()
      );
    }

    if (this.productBrand) {
      query.push(builder.push(["product.merk", this.productBrand]).build());
    }

    return query.join(builder.AND);
  }
}
